import React from "react";
import { useLanguage } from "../../components/LanguageContext";
import { localized, pick } from "../../utils/localization";

// URL của Cloudflare Worker (đặt trong .env → REACT_APP_CHAT_ENDPOINT).
const CHAT_ENDPOINT = process.env.REACT_APP_CHAT_ENDPOINT || "";

const copy = {
    launcher: localized("Chat with Tam's assistant", "Chat với trợ lý của Tâm"),
    title: localized("Tam's Assistant", "Trợ lý của Tâm"),
    subtitle: localized("Ask about skills, projects, experience", "Hỏi về kỹ năng, dự án, kinh nghiệm"),
    greeting: localized(
        "Hi! I'm Tam's assistant. Ask me anything about his backend skills, projects, or experience. 👋",
        "Xin chào! Mình là trợ lý của anh Tâm. Bạn cứ hỏi mình về kỹ năng backend, dự án hay kinh nghiệm của anh ấy nhé. 👋"
    ),
    placeholder: localized("Type your question…", "Nhập câu hỏi của bạn…"),
    send: localized("Send", "Gửi"),
    close: localized("Close chat", "Đóng chat"),
    thinking: localized("Typing…", "Đang trả lời…"),
    error: localized(
        "Sorry, something went wrong. Please try again, or email ngoctam2303001@gmail.com.",
        "Xin lỗi, có lỗi xảy ra. Bạn thử lại nhé, hoặc email ngoctam2303001@gmail.com."
    ),
    disclaimer: localized("Usually replies in a few seconds", "Thường phản hồi trong vài giây"),
};

const suggestions = [
    localized("Backend skills?", "Kỹ năng backend?"),
    localized("Work experience?", "Kinh nghiệm làm việc?"),
    localized("Featured projects?", "Dự án tiêu biểu?"),
    localized("How to contact?", "Cách liên hệ?"),
];

// Chuyển **đậm** trong một dòng thành node React (an toàn, không dùng innerHTML).
const renderInline = (text) =>
    text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
        const bold = /^\*\*([^*]+)\*\*$/.exec(part);
        if (bold) return <strong key={i}>{bold[1]}</strong>;
        return <React.Fragment key={i}>{part}</React.Fragment>;
    });

// Render nội dung tin nhắn: hỗ trợ xuống dòng và danh sách "- " để không bị dồn
// cục. Model được nhắc không dùng markdown, nhưng vẫn xử lý phòng khi lỡ có.
const renderRich = (text) => {
    const lines = text.replace(/\r/g, "").split("\n");
    const blocks = [];
    let list = null;
    for (const line of lines) {
        const trimmed = line.trim();
        const bullet = /^[-*•]\s+(.*)/.exec(trimmed);
        if (bullet) {
            if (!list) list = [];
            list.push(bullet[1]);
        } else {
            if (list) {
                blocks.push({ type: "ul", items: list });
                list = null;
            }
            if (trimmed) blocks.push({ type: "p", text: trimmed });
        }
    }
    if (list) blocks.push({ type: "ul", items: list });

    return blocks.map((b, i) =>
        b.type === "ul" ? (
            <ul key={i} className="portfolio-chat-list">
                {b.items.map((it, j) => (
                    <li key={j}>{renderInline(it)}</li>
                ))}
            </ul>
        ) : (
            <p key={i}>{renderInline(b.text)}</p>
        )
    );
};

const ChatIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
            d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v7A2.5 2.5 0 0 1 17.5 15H9l-4 4v-4H6.5A2.5 2.5 0 0 1 4 12.5v-7Z"
            stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"
        />
        <circle cx="9" cy="9" r="1" fill="currentColor" />
        <circle cx="12.5" cy="9" r="1" fill="currentColor" />
        <circle cx="16" cy="9" r="1" fill="currentColor" />
    </svg>
);

const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

const SendIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 12l16-8-6 16-3-7-7-1Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
);

const PortfolioChat = () => {
    const { language } = useLanguage();
    const [open, setOpen] = React.useState(false);
    const [messages, setMessages] = React.useState([]);
    const [input, setInput] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const listRef = React.useRef(null);
    const inputRef = React.useRef(null);

    // Seed the greeting the first time the panel opens.
    React.useEffect(() => {
        if (open && messages.length === 0) {
            setMessages([{ role: "assistant", text: pick(copy.greeting, language), intro: true }]);
        }
        if (open && inputRef.current) {
            inputRef.current.focus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Keep the transcript scrolled to the latest message.
    React.useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [messages, loading]);

    // Đổi ngôn ngữ UI → dịch lại ngay bong bóng chào (canned). Các câu trả lời
    // cũ giữ nguyên; câu trả lời MỚI sẽ theo ngôn ngữ mới (đã gửi lang xuống Worker).
    React.useEffect(() => {
        setMessages((prev) =>
            prev.map((m) => (m.intro ? { ...m, text: pick(copy.greeting, language) } : m))
        );
    }, [language]);

    // Close on Escape.
    React.useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    const send = async (raw) => {
        const message = (raw ?? input).toString().trim();
        if (!message || loading) return;

        // History = real exchanges so far (exclude the canned intro + errors).
        const history = messages
            .filter((m) => !m.intro && !m.isError)
            .map((m) => ({ role: m.role, text: m.text }));

        setMessages((prev) => [...prev, { role: "user", text: message }]);
        setInput("");
        setLoading(true);

        try {
            if (!CHAT_ENDPOINT) {
                throw new Error("missing-endpoint");
            }
            const res = await fetch(CHAT_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ message, history, lang: language }),
            });
            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                // Worker trả {error: "..."} kèm thông điệp thân thiện (rate-limit, v.v.).
                const serverMsg = data?.error?.toString().trim();
                setMessages((prev) => [
                    ...prev,
                    { role: "assistant", text: serverMsg || pick(copy.error, language), isError: true },
                ]);
                return;
            }

            const reply = data?.reply?.toString().trim();
            setMessages((prev) => [
                ...prev,
                reply
                    ? { role: "assistant", text: reply }
                    : { role: "assistant", text: pick(copy.error, language), isError: true },
            ]);
        } catch (err) {
            // Log chi tiết để chẩn đoán (chưa cấu hình endpoint, Worker chưa deploy,
            // CORS, mạng…).
            // eslint-disable-next-line no-console
            console.error("[chat] request failed:", err?.message, err);
            setMessages((prev) => [
                ...prev,
                { role: "assistant", text: pick(copy.error, language), isError: true },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        send();
    };

    const showSuggestions = open && messages.filter((m) => m.role === "user").length === 0;

    return (
        <div className={`portfolio-chat${open ? " is-open" : ""}`}>
            {open && (
                <section
                    className="portfolio-chat-panel"
                    role="dialog"
                    aria-label={pick(copy.title, language)}
                >
                    <header className="portfolio-chat-head">
                        <div className="portfolio-chat-head-id">
                            <span className="portfolio-chat-avatar" aria-hidden="true">T</span>
                            <div>
                                <strong>{pick(copy.title, language)}</strong>
                                <span>{pick(copy.subtitle, language)}</span>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="portfolio-chat-close"
                            onClick={() => setOpen(false)}
                            aria-label={pick(copy.close, language)}
                        >
                            <CloseIcon />
                        </button>
                    </header>

                    <div className="portfolio-chat-log" ref={listRef}>
                        {messages.map((m, i) => (
                            <div
                                key={i}
                                className={`portfolio-chat-msg is-${m.role}${m.isError ? " is-error" : ""}`}
                            >
                                {renderRich(m.text)}
                            </div>
                        ))}

                        {loading && (
                            <div className="portfolio-chat-msg is-assistant portfolio-chat-typing" aria-live="polite">
                                <span></span><span></span><span></span>
                                <span className="sr-only">{pick(copy.thinking, language)}</span>
                            </div>
                        )}

                        {showSuggestions && (
                            <div className="portfolio-chat-suggestions">
                                {suggestions.map((s, i) => (
                                    <button key={i} type="button" onClick={() => send(pick(s, language))}>
                                        {pick(s, language)}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <form className="portfolio-chat-input" onSubmit={onSubmit}>
                        <input
                            ref={inputRef}
                            type="text"
                            value={input}
                            maxLength={1000}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={pick(copy.placeholder, language)}
                            aria-label={pick(copy.placeholder, language)}
                        />
                        <button
                            type="submit"
                            disabled={loading || !input.trim()}
                            aria-label={pick(copy.send, language)}
                        >
                            <SendIcon />
                        </button>
                    </form>
                    <p className="portfolio-chat-disclaimer">{pick(copy.disclaimer, language)}</p>
                </section>
            )}

            <button
                type="button"
                className="portfolio-chat-launcher"
                onClick={() => setOpen((v) => !v)}
                aria-label={pick(copy.launcher, language)}
                aria-expanded={open}
            >
                {open ? <CloseIcon /> : <ChatIcon />}
            </button>
        </div>
    );
};

export default PortfolioChat;
